import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Square, Video } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { saveBlobsToIndexedDB, deleteBlobsByQuestionId } from "@/lib/blobStorage";
import { uploadQueue } from "@/lib/uploadQueue";

// Helper function to get supported video MIME type - PRIORITIZE MP4 for iOS
const getSupportedVideoMimeType = (): string | undefined => {
  const types = [
    'video/mp4',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4;codecs=h264,aac',
  ];
  return types.find(type => MediaRecorder.isTypeSupported(type));
};

// Helper function to get supported audio MIME type
const getSupportedAudioMimeType = (): string | undefined => {
  const types = [
    'audio/mp4',
    'audio/aac',
    'audio/webm;codecs=opus',
    'audio/webm',
  ];
  return types.find(type => MediaRecorder.isTypeSupported(type));
};

interface VideoRecorderProps {
  onRecordingComplete: (payload: any) => void;
  testResponseId?: string;
  questionId?: string;
  onRecordingStart?: () => void;
  onRecordingStop?: () => void;
  onUploadProgress?: (questionId: string, status: 'pending' | 'complete' | 'failed', type: 'video' | 'audio') => void;
  audioDeviceId?: string;
}

export interface VideoRecorderHandle {
  stopRecordingAndUpload: () => Promise<void>;
  isRecording: () => boolean;
  isUploading: () => boolean;
}

export const VideoRecorder = forwardRef<VideoRecorderHandle, VideoRecorderProps>(
  ({ onRecordingComplete, testResponseId, questionId, onRecordingStart, onRecordingStop, onUploadProgress, audioDeviceId }, ref) => {
    const { toast } = useToast();
    const [isRecording, setIsRecording] = useState(false);
    const [isPreparing, setIsPreparing] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [actualVideoMimeType, setActualVideoMimeType] = useState<string>('video/webm');
    const [actualAudioMimeType, setActualAudioMimeType] = useState<string>('audio/webm');
    
    const videoMediaRecorderRef = useRef<MediaRecorder | null>(null);
    const videoStreamRef = useRef<MediaStream | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const videoChunksRef = useRef<Blob[]>([]);
    
    const audioMediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioStreamRef = useRef<MediaStream | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);

    const isUploadingRef = useRef(false);
    const isRecordingRef = useRef(false);
    const currentUploadIdRef = useRef<string | null>(null);

    // Use refs for callbacks to avoid stale closure issues in subscription
    const onRecordingCompleteRef = useRef(onRecordingComplete);
    const onUploadProgressRef = useRef(onUploadProgress);

    // Keep refs updated when callbacks change
    useEffect(() => {
      onRecordingCompleteRef.current = onRecordingComplete;
    }, [onRecordingComplete]);

    useEffect(() => {
      onUploadProgressRef.current = onUploadProgress;
    }, [onUploadProgress]);

    // Detect mobile devices
    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent));
      };
      checkMobile();
      window.addEventListener('resize', checkMobile);
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
      return () => {
        if (videoStreamRef.current) {
          videoStreamRef.current.getTracks().forEach(track => track.stop());
        }
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(track => track.stop());
        }
      };
    }, []);

    // Reset all internal state when switching questions (belt-and-suspenders with key prop)
    useEffect(() => {
      console.log('[VideoRecorder] Question changed, resetting internal state');
      setIsUploading(false);
      isUploadingRef.current = false;
      currentUploadIdRef.current = null;
      setIsRecording(false);
      isRecordingRef.current = false;
      setIsPreparing(false);
      videoChunksRef.current = [];
      audioChunksRef.current = [];
    }, [questionId]);

    // Subscribe to upload queue for this question's upload status - use refs for callbacks
    useEffect(() => {
      if (!questionId) return;

      const unsubscribe = uploadQueue.subscribe((state) => {
        const upload = state.uploads.find(u => u.questionId === questionId);
        if (upload && currentUploadIdRef.current === upload.id) {
          if (upload.status === 'complete') {
            // Upload complete - notify parent with paths using refs for latest callbacks
            console.log('[VideoRecorder] Upload complete, calling onRecordingComplete via ref');
            onRecordingCompleteRef.current({
              videoPath: upload.videoPath,
              audioPath: upload.audioPath,
              uploadId: upload.id,
            });
            setIsUploading(false);
            isUploadingRef.current = false;
            if (questionId) {
              console.log('[VideoRecorder] Calling onUploadProgress via ref: complete');
              onUploadProgressRef.current?.(questionId, 'complete', 'video');
            }
          } else if (upload.status === 'failed') {
            if (questionId) {
              console.log('[VideoRecorder] Calling onUploadProgress via ref: failed');
              onUploadProgressRef.current?.(questionId, 'failed', 'video');
            }
            // Reset isUploading so user can re-record after failure
            console.log('[VideoRecorder] Upload failed - resetting isUploading to allow re-recording');
            setIsUploading(false);
            isUploadingRef.current = false;
          }
        }
      });

      return unsubscribe;
    }, [questionId]); // Remove onRecordingComplete and onUploadProgress from deps - using refs

    // Helper function to get media with progressive fallbacks and retry logic
    const getMediaWithRetry = async (): Promise<MediaStream> => {
      const maxAttempts = 3;
      const baseDelay = 500;
      
      // Build constraint strategies - prefer selected audio device but fallback gracefully
      const getConstraintStrategies = () => {
        const videoConstraints = {
          width: { ideal: isMobile ? 640 : 1280 },
          height: { ideal: isMobile ? 480 : 720 },
          frameRate: { ideal: 24, max: 30 },
          facingMode: "user"
        };
        
        const strategies = [];
        
        // Strategy 1: With preferred audio device (not exact)
        if (audioDeviceId) {
          strategies.push({
            video: videoConstraints,
            audio: { deviceId: { ideal: audioDeviceId } }
          });
        }
        
        // Strategy 2: Default audio
        strategies.push({
          video: videoConstraints,
          audio: true
        });
        
        // Strategy 3: Minimal constraints (absolute fallback)
        strategies.push({
          video: true,
          audio: true
        });
        
        return strategies;
      };
      
      let lastError: any = null;
      
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const strategies = getConstraintStrategies();
        
        for (let strategyIdx = 0; strategyIdx < strategies.length; strategyIdx++) {
          const constraints = strategies[strategyIdx];
          
          try {
            console.log(`[VideoRecorder] Attempt ${attempt + 1}, strategy ${strategyIdx + 1}:`, JSON.stringify(constraints));
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            console.log(`[VideoRecorder] Success on attempt ${attempt + 1}, strategy ${strategyIdx + 1}`);
            return stream;
          } catch (err: any) {
            console.warn(`[VideoRecorder] Attempt ${attempt + 1}, strategy ${strategyIdx + 1} failed:`, err.name, err.message);
            lastError = err;
            
            // Don't retry or fallback for permission denied - that won't help
            if (err.name === 'NotAllowedError') {
              throw err;
            }
            
            // For NotReadableError (camera busy), try next strategy immediately
            // then retry with delay if all strategies exhausted
            if (err.name === 'NotReadableError' && strategyIdx === strategies.length - 1 && attempt < maxAttempts - 1) {
              const delay = baseDelay * (attempt + 1);
              console.log(`[VideoRecorder] Camera busy, waiting ${delay}ms before retry...`);
              await new Promise(r => setTimeout(r, delay));
            }
            
            // Continue to next strategy
            continue;
          }
        }
      }
      
      throw lastError;
    };

    const startRecording = async () => {
      const safetyTimeout = setTimeout(() => {
        if (isPreparing) {
          console.error('[VideoRecorder] Setup timeout - resetting state');
          setIsPreparing(false);
          toast({
            title: "Recording setup timeout",
            description: "Camera setup took too long. Please try again.",
            variant: "destructive"
          });
        }
      }, 15000); // Increased timeout for retries

      try {
        setIsPreparing(true);
        videoChunksRef.current = [];
        audioChunksRef.current = [];
        
        console.log('[VideoRecorder] Starting recording, mobile:', isMobile);
        
        // Signal that recording is about to start
        onRecordingStart?.();
        
        // Wait for monitoring camera to release on mobile (increased delay)
        if (isMobile) {
          console.log('[VideoRecorder] Waiting for monitoring camera to release...');
          await new Promise(resolve => setTimeout(resolve, 2000)); // Increased from 1500ms
        }
        
        console.log('[VideoRecorder] Requesting media with audio device:', audioDeviceId || 'default');
        
        // Use progressive fallback with retry logic
        const stream = await getMediaWithRetry();
        
        videoStreamRef.current = stream;
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(e => console.warn("Preview play interrupted", e));
        }

        const audioTrack = stream.getAudioTracks()[0];
        if (!audioTrack) throw new Error('No audio track available');
        const audioStream = new MediaStream([audioTrack]);
        audioStreamRef.current = audioStream;

        const vMime = getSupportedVideoMimeType() || 'video/webm';
        const aMime = getSupportedAudioMimeType() || 'audio/webm';
        setActualVideoMimeType(vMime);
        setActualAudioMimeType(aMime);

        console.log('[VideoRecorder] Using MIME types - Video:', vMime, 'Audio:', aMime);

        const videoBitrate = isMobile ? 800_000 : 1_500_000;
        const videoBitrateAudio = 96_000;
        const audioBitrate = 64_000;

        const videoRecorder = new MediaRecorder(stream, { 
          mimeType: vMime,
          videoBitsPerSecond: videoBitrate,
          audioBitsPerSecond: videoBitrateAudio
        });
        const audioRecorder = new MediaRecorder(audioStream, { 
          mimeType: aMime,
          audioBitsPerSecond: audioBitrate
        });

        videoMediaRecorderRef.current = videoRecorder;
        audioMediaRecorderRef.current = audioRecorder;

        videoRecorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) videoChunksRef.current.push(e.data);
        };
        audioRecorder.ondataavailable = (e) => {
          if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        videoRecorder.start(1000);
        await new Promise(r => setTimeout(r, 200)); 
        audioRecorder.start(1000);

        setIsRecording(true);
        isRecordingRef.current = true;
        setIsPreparing(false);
        clearTimeout(safetyTimeout);

      } catch (error: any) {
        clearTimeout(safetyTimeout);
        console.error("[VideoRecorder] Error starting recording:", error.name, error.message);
        setIsPreparing(false);
        
        if (videoStreamRef.current) {
          videoStreamRef.current.getTracks().forEach(track => track.stop());
          videoStreamRef.current = null;
        }
        if (audioStreamRef.current) {
          audioStreamRef.current.getTracks().forEach(track => track.stop());
          audioStreamRef.current = null;
        }
        
        let description = "Could not access camera. Please reset permissions or refresh.";
        
        if (error.name === 'NotReadableError') {
          description = "Camera is busy or unavailable. Please close other apps using your camera and try again.";
        } else if (error.name === 'NotAllowedError') {
          description = "Camera permission was denied. Please allow camera access in your browser settings.";
        } else if (error.name === 'NotFoundError') {
          description = "No camera found on this device.";
        } else if (error.name === 'OverconstrainedError') {
          description = "Camera doesn't support required settings. Please try a different camera.";
        }
        
        toast({
          title: "Recording failed",
          description,
          variant: "destructive"
        });
      }
    };

    const stopRecorder = (
      recorder: MediaRecorder,
      chunks: Blob[],
      mimeType: string,
      stream: MediaStream | null
    ): Promise<Blob | null> => {
      return new Promise((resolve) => {
        recorder.onstop = () => {
          const blob = new Blob(chunks, { type: mimeType });
          chunks.length = 0;
          
          if (stream) {
            stream.getTracks().forEach(track => track.stop());
          }
          
          resolve(blob.size > 0 ? blob : null);
        };
        
        if (recorder.state !== 'inactive') {
          recorder.stop();
        } else {
          resolve(null);
        }
      });
    };

    const stopRecordingAndUpload = async (): Promise<void> => {
      if (!isRecordingRef.current || !videoMediaRecorderRef.current) {
        console.log('[VideoRecorder] No active recording to stop');
        return;
      }

      if (!testResponseId || !questionId) {
        console.error('[VideoRecorder] Missing testResponseId or questionId');
        toast({
          title: "Upload Error",
          description: "Missing test information. Please refresh and try again.",
          variant: "destructive",
        });
        return;
      }

      console.log('[VideoRecorder] Stopping recording and queueing for TUS upload');
      setIsRecording(false);
      isRecordingRef.current = false;
      setIsUploading(true);
      isUploadingRef.current = true;
      onRecordingStop?.();

      try {
        // Step 1: Stop recorders and get blobs
        const [videoBlob, audioBlob] = await Promise.all([
          stopRecorder(videoMediaRecorderRef.current, videoChunksRef.current, actualVideoMimeType, videoStreamRef.current),
          stopRecorder(audioMediaRecorderRef.current!, audioChunksRef.current, actualAudioMimeType, audioStreamRef.current)
        ]);

        // Log blob sizes
        const videoSizeMB = (videoBlob?.size || 0) / (1024 * 1024);
        const audioSizeMB = (audioBlob?.size || 0) / (1024 * 1024);
        console.log('[VideoRecorder] Blob sizes:', {
          video: `${videoSizeMB.toFixed(2)} MB`,
          audio: `${audioSizeMB.toFixed(2)} MB`
        });

        // Clean up video element
        if (videoRef.current) {
          videoRef.current.srcObject = null;
        }

        // Step 2: Create preview URL and show immediately
        const previewUrl = videoBlob ? URL.createObjectURL(videoBlob) : undefined;
        console.log('[VideoRecorder] Showing preview immediately');
        onRecordingComplete({ previewUrl });

        // Step 3: Clear any previous uploads for this question from BOTH queue and IndexedDB
        uploadQueue.removeByQuestionId(questionId);
        await deleteBlobsByQuestionId(questionId);

        // Step 4: Generate upload ID BEFORE enqueue to prevent race condition
        // Fast uploads can complete before enqueue returns, so we need the ID first
        const preGeneratedUploadId = crypto.randomUUID();
        currentUploadIdRef.current = preGeneratedUploadId;
        console.log('[VideoRecorder] Pre-generated upload ID:', preGeneratedUploadId);

        // Step 5: Signal pending upload
        if (questionId) {
          onUploadProgress?.(questionId, 'pending', 'video');
          console.log('[VideoRecorder] Registered pending upload before enqueue');
        }

        // Step 6: Save to IndexedDB IMMEDIATELY (crash-safe)
        // Then enqueue for TUS upload with pre-generated ID
        console.log('[VideoRecorder] Saving to IndexedDB and queueing TUS upload');
        const uploadId = await uploadQueue.enqueueWithId(
          preGeneratedUploadId,
          questionId,
          testResponseId,
          videoBlob || undefined,
          audioBlob || undefined,
          actualVideoMimeType,
          actualAudioMimeType
        );

        console.log('[VideoRecorder] Upload queued with ID:', uploadId);
        
        // The upload will proceed in background via uploadQueue
        // When complete, the subscription effect above will be triggered

      } catch (error) {
        console.error('[VideoRecorder] Error during recording stop:', error);
        setIsUploading(false);
        isUploadingRef.current = false;
        toast({
          title: "Error",
          description: "Could not process recording. Please try again.",
          variant: "destructive",
        });
      }
    };

    useImperativeHandle(ref, () => ({
      stopRecordingAndUpload,
      isRecording: () => isRecordingRef.current,
      isUploading: () => isUploadingRef.current
    }), [stopRecordingAndUpload]);

    return (
      <div className="space-y-4">
        <div className="relative aspect-video bg-muted rounded-sm overflow-hidden">
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="w-full h-full object-cover"
          />
          {isRecording && (
            <div className="absolute top-2 right-2 flex items-center space-x-2 bg-black/50 rounded-full px-3 py-1">
              <span className="animate-pulse h-2 w-2 rounded-full bg-red-500"></span>
              <span className="text-xs text-white">Recording</span>
            </div>
          )}
        </div>

        <div className="flex justify-center">
          {!isRecording ? (
            <Button
              onClick={startRecording}
              disabled={isPreparing || isUploading}
              variant="secondary"
              className="w-40"
            >
              {isPreparing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Video className="h-4 w-4 mr-2" />
                  Start Recording
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={stopRecordingAndUpload}
              variant="destructive"
              disabled={isUploading}
              className="w-40"
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Square className="h-4 w-4 mr-2" />
                  Stop
                </>
              )}
            </Button>
          )}
        </div>
        
        {isUploading && (
          <div className="text-center text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
            Uploading in background...
          </div>
        )}
      </div>
    );
  }
);

VideoRecorder.displayName = "VideoRecorder";
